import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtINR, fmtINRFull, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ChevronDown, ChevronRight, Plus, Save, Send, Check, X, Trash2, RefreshCw, Search, Calculator, FlaskConical, Download } from "lucide-react";
import ExcelImport, { parseRA as parseRASheet } from "@/components/ExcelImport";
import { exportBOQToXlsx } from "@/lib/exportBOQ";

const RA_TYPES = ["Material", "Labour", "Agency", "SOH", "P&M", "Escalation", "Reduction"];

function derived(l) {
  const amount = (l.qty || 0) * (l.rate || 0);
  const jcrAmount = (l.jcr_qty || 0) * (l.jcr_rate || 0);
  const balanceQty = Math.max(0, (l.qty || 0) - (l.jcr_qty || 0));
  const balanceAmount = balanceQty * (l.rate || 0);
  // CTC: qty remaining based on GFC (if set) else same as balance
  const ctcQty = (l.gfc_qty || 0) > 0 ? Math.max(0, (l.gfc_qty || 0) - (l.jcr_qty || 0)) : balanceQty;
  const ctcAmount = ctcQty * (l.rate || 0);
  return { amount, jcrAmount, balanceQty, balanceAmount, ctcQty, ctcAmount };
}

export default function BOQEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const [boq, setBoq] = useState(null);
  const [proj, setProj] = useState(null);
  const [raMap, setRaMap] = useState({}); // iow_code -> {entries, ra_rate}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [openCat, setOpenCat] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveRemark, setApproveRemark] = useState("");
  const [raOpen, setRaOpen] = useState(false);
  const [raIow, setRaIow] = useState(null);

  const canEdit = ["admin", "budget_controller"].includes(user?.role);
  const canApprove = ["admin", "engineering_head", "chairman"].includes(user?.role);

  const load = async () => {
    const [b, p, ra] = await Promise.all([
      api.get(`/projects/${id}/boq`),
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/rate-analysis`),
    ]);
    setBoq(b.data); setProj(p.data); setRaMap(ra.data || {});
  };
  useEffect(() => { load(); }, [id]);

  const grouped = useMemo(() => {
    if (!boq?.lines) return [];
    const map = {};
    for (const l of boq.lines) {
      const cat = l.category || "Uncategorized";
      const sub = l.sub_category || "";
      map[cat] = map[cat] || {};
      map[cat][sub] = map[cat][sub] || [];
      map[cat][sub].push(l);
    }
    return Object.entries(map).map(([cat, subs]) => ({ cat, subs: Object.entries(subs) }));
  }, [boq]);

  const totals = useMemo(() => {
    const lines = boq?.lines || [];
    const t = { budget: 0, jcr: 0, balance: 0, ctc: 0 };
    for (const l of lines) {
      const d = derived(l);
      t.budget += d.amount; t.jcr += d.jcrAmount;
      t.balance += d.balanceAmount; t.ctc += d.ctcAmount;
    }
    return t;
  }, [boq]);

  const updateLine = (idx, patch) => {
    if (!canEdit || boq?.status !== "Draft") return;
    const lines = [...(boq.lines || [])];
    lines[idx] = { ...lines[idx], ...patch };
    setBoq({ ...boq, lines });
  };
  const removeLine = (idx) => {
    if (!canEdit || boq?.status !== "Draft") return;
    const lines = [...(boq.lines || [])];
    lines.splice(idx, 1);
    setBoq({ ...boq, lines });
  };
  const addFromSOR = (sor) => {
    if (!canEdit || boq?.status !== "Draft") return;
    const lines = [...(boq?.lines || []), {
      iow_code: sor.iow_code, description: sor.item_of_work, uom: sor.uom,
      category: sor.category || "General", sub_category: sor.sub_category || "",
      qty: 0, rate: sor.rate, gfc_qty: 0, jcr_qty: 0, jcr_rate: 0, has_ra: false,
    }];
    setBoq({ ...(boq || {}), lines });
    setPickerOpen(false);
  };

  const saveDraft = async () => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.post(`/projects/${id}/boq`, { lines: boq.lines });
      setBoq(data); setMsg("Draft saved.");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async () => {
    setSaving(true); setMsg("");
    try {
      await api.post(`/projects/${id}/boq`, { lines: boq.lines });
      const { data } = await api.post(`/projects/${id}/boq/submit`);
      setBoq(data); setMsg("Submitted for approval.");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const approve = async () => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.post(`/projects/${id}/boq/approve`, { remark: approveRemark });
      setBoq(data); setMsg("Approved.");
      setApproveOpen(false); setApproveRemark("");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reject = async () => {
    if (!rejectRemark.trim()) return;
    setSaving(true); setMsg("");
    try {
      const { data } = await api.post(`/projects/${id}/boq/reject`, { remark: rejectRemark });
      setBoq(data); setRejectOpen(false); setRejectRemark(""); setMsg("Rejected.");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const revise = async () => {
    setSaving(true); setMsg("");
    try { const { data } = await api.post(`/projects/${id}/boq/revise`); setBoq(data); setMsg("New draft created."); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openRA = (iow) => { setRaIow(iow); setRaOpen(true); };
  const onRASaved = (iow, ra) => {
    setRaMap((m) => ({ ...m, [iow]: ra }));
  };
  const applyRARate = (iow, rate) => {
    const idx = (boq.lines || []).findIndex((l) => l.iow_code === iow);
    if (idx >= 0) updateLine(idx, { rate });
  };

  if (!boq) return <div className="text-slate-500">Loading…</div>;
  const status = boq.status || "Draft";
  const editable = canEdit && status === "Draft";

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/projects/${id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0D3B66]"><ArrowLeft className="h-4 w-4" /> Back to project</Link>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">BOQ Editor</h1>
            <p className="mt-1.5 text-slate-500">{proj?.name} · Version {boq.version || 0} · <StatusChip status={status} /></p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid="export-boq-btn"
              variant="outline"
              className="rounded-full"
              onClick={() => exportBOQToXlsx({ project: proj, boq, raMap })}
              disabled={!boq || (boq.lines || []).length === 0}
            >
              <Download className="h-4 w-4 mr-1.5" /> Export Excel
            </Button>
            {editable && (
              <>
                <ExcelImport
                  mode="boq"
                  buttonLabel="Import Excel"
                  onImport={async (lines, replace, wb) => {
                    // Import BOQ
                    const { data } = await api.post(`/projects/${id}/boq/import`, { lines, replace });
                    setBoq(data);
                    setMsg(`Imported ${lines.length} lines.`);
                    // Try to also import RA sheet from the same workbook
                    if (wb) {
                      try {
                        const raParsed = parseRASheet(wb);
                        let raCount = 0;
                        for (const [iow, entries] of Object.entries(raParsed)) {
                          if (!entries.length) continue;
                          const cleaned = entries.filter((e) => RA_TYPES.includes(e.type));
                          if (!cleaned.length) continue;
                          await api.put(`/projects/${id}/rate-analysis/${iow}`, { entries: cleaned });
                          raCount++;
                        }
                        if (raCount > 0) {
                          const { data: ra } = await api.get(`/projects/${id}/rate-analysis`);
                          setRaMap(ra || {});
                          setMsg(`Imported ${lines.length} BOQ lines + ${raCount} rate analyses.`);
                        }
                      } catch (e) { /* silent */ }
                    }
                  }}
                />
                <Button data-testid="add-line-btn" variant="outline" className="rounded-full" onClick={() => setPickerOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Add line
                </Button>
                <Button data-testid="save-draft-btn" variant="outline" className="rounded-full" onClick={saveDraft} disabled={saving}>
                  <Save className="h-4 w-4 mr-1.5" /> Save draft
                </Button>
                <Button data-testid="submit-boq-btn" className="rounded-full bg-[#F97316] hover:bg-[#EA6A0E]" onClick={submit} disabled={saving || (boq.lines || []).length === 0}>
                  <Send className="h-4 w-4 mr-1.5" /> Submit
                </Button>
              </>
            )}
            {status === "Submitted" && canApprove && (
              <>
                <Button data-testid="approve-boq-btn" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setApproveOpen(true)} disabled={saving}>
                  <Check className="h-4 w-4 mr-1.5" /> Approve
                </Button>
                <Button data-testid="reject-boq-btn" variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setRejectOpen(true)} disabled={saving}>
                  <X className="h-4 w-4 mr-1.5" /> Reject
                </Button>
              </>
            )}
            {(status === "Approved" || status === "Rejected") && canEdit && (
              <Button data-testid="revise-boq-btn" variant="outline" className="rounded-full" onClick={revise} disabled={saving}>
                <RefreshCw className="h-4 w-4 mr-1.5" /> Start revision
              </Button>
            )}
          </div>
        </div>
        {msg && <div data-testid="boq-msg" className="mt-3 text-sm text-slate-600">{msg}</div>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TotalCard label="Budget" value={totals.budget} accent="text-[#0D3B66]" testId="total-budget" />
        <TotalCard label="Actual (JCR)" value={totals.jcr} accent="text-emerald-700" testId="total-jcr" />
        <TotalCard label="Balance" value={totals.balance} accent="text-amber-700" testId="total-balance" />
        <TotalCard label="CTC" value={totals.ctc} accent="text-[#F97316]" testId="total-ctc" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1400px] w-full text-xs" data-testid="boq-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10 min-w-[90px]">IOW</th>
                <th className="px-3 py-2 text-left min-w-[240px]">Description</th>
                <th className="px-3 py-2 text-left min-w-[60px]">UOM</th>
                <th className="px-3 py-2 text-center bg-blue-50/50 min-w-[220px]" colSpan={3}>Budget</th>
                <th className="px-3 py-2 text-right min-w-[80px]">GFC Qty</th>
                <th className="px-3 py-2 text-center bg-emerald-50/50 min-w-[220px]" colSpan={3}>Actual (JCR)</th>
                <th className="px-3 py-2 text-right min-w-[110px]">Balance Amt</th>
                <th className="px-3 py-2 text-right min-w-[110px]">CTC Amt</th>
                <th className="px-3 py-2 text-center min-w-[80px]">RA</th>
                {editable && <th className="px-3 py-2 min-w-[40px]"></th>}
              </tr>
              <tr className="bg-slate-50/70 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <th className="sticky left-0 bg-slate-50/70 z-10"></th>
                <th></th><th></th>
                <th className="px-3 py-1.5 text-right bg-blue-50/40">Qty</th>
                <th className="px-3 py-1.5 text-right bg-blue-50/40">Rate</th>
                <th className="px-3 py-1.5 text-right bg-blue-50/40">Amount</th>
                <th></th>
                <th className="px-3 py-1.5 text-right bg-emerald-50/40">Qty</th>
                <th className="px-3 py-1.5 text-right bg-emerald-50/40">Rate</th>
                <th className="px-3 py-1.5 text-right bg-emerald-50/40">Amount</th>
                <th></th><th></th><th></th>
                {editable && <th></th>}
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td colSpan={editable ? 14 : 13} data-testid="boq-empty" className="p-10 text-center text-slate-500">No lines yet. Click &ldquo;Import Excel&rdquo; or &ldquo;Add line&rdquo; to start.</td></tr>
              )}
              {grouped.map(({ cat, subs }) => (
                <RowGroup
                  key={cat}
                  cat={cat}
                  subs={subs}
                  boq={boq}
                  editable={editable}
                  raMap={raMap}
                  openCat={openCat}
                  setOpenCat={setOpenCat}
                  updateLine={updateLine}
                  removeLine={removeLine}
                  openRA={openRA}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SORPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={addFromSOR} />

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject Budget</DialogTitle></DialogHeader>
          <div className="space-y-2 pt-2">
            <Label>Remark (required)</Label>
            <Textarea data-testid="reject-remark-input" value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} rows={4} placeholder="Reason for rejection…" />
          </div>
          <DialogFooter>
            <Button data-testid="reject-confirm-btn" onClick={reject} disabled={!rejectRemark.trim() || saving} className="bg-rose-600 hover:bg-rose-700">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader><DialogTitle>Approve Budget</DialogTitle></DialogHeader>
          <div className="space-y-2 pt-2">
            <Label>Remark (optional)</Label>
            <Textarea data-testid="approve-remark-input" value={approveRemark} onChange={(e) => setApproveRemark(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button data-testid="approve-confirm-btn" onClick={approve} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RateAnalysisDialog
        open={raOpen}
        onOpenChange={setRaOpen}
        projectId={id}
        iowCode={raIow}
        line={raIow ? (boq.lines || []).find((l) => l.iow_code === raIow) : null}
        editable={editable}
        initial={raIow ? raMap[raIow] : null}
        onSaved={(ra) => onRASaved(raIow, ra)}
        onApplyRate={(rate) => applyRARate(raIow, rate)}
      />
    </div>
  );
}

function TotalCard({ label, value, accent, testId }) {
  return (
    <Card data-testid={testId} className="p-4 border-slate-200 shadow-none">
      <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tracking-tight ${accent}`}>{fmtINR(value)}</div>
    </Card>
  );
}

function RowGroup({ cat, subs, boq, editable, raMap, openCat, setOpenCat, updateLine, removeLine, openRA }) {
  const open = openCat[cat] !== false;
  const colSpan = editable ? 14 : 13;
  return (
    <>
      <tr className="bg-[#0D3B66]/5 border-b border-slate-200">
        <td colSpan={colSpan} className="px-3 py-2 sticky left-0">
          <button
            onClick={() => setOpenCat((s) => ({ ...s, [cat]: !open }))}
            data-testid={`boq-cat-${cat.replace(/\W+/g, "-")}`}
            className="flex items-center gap-2 text-sm font-semibold text-[#0D3B66]"
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {cat}
          </button>
        </td>
      </tr>
      {open && subs.map(([sub, lines]) => (
        <RowSub
          key={sub}
          sub={sub}
          lines={lines}
          boq={boq}
          editable={editable}
          raMap={raMap}
          updateLine={updateLine}
          removeLine={removeLine}
          openRA={openRA}
        />
      ))}
    </>
  );
}

function RowSub({ sub, lines, boq, editable, raMap, updateLine, removeLine, openRA }) {
  return (
    <>
      {sub && (
        <tr className="bg-slate-50/60 border-b border-slate-100">
          <td colSpan={editable ? 14 : 13} className="px-3 py-1.5 sticky left-0 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{sub}</td>
        </tr>
      )}
      {lines.map((l) => {
        const idx = boq.lines.indexOf(l);
        const d = derived(l);
        const ra = raMap[l.iow_code];
        return (
          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/40 text-xs">
            <td className="px-3 py-1.5 font-mono text-slate-700 sticky left-0 bg-white">{l.iow_code}</td>
            <td className="px-3 py-1.5 text-slate-800">
              <div className="max-w-[380px] truncate" title={l.description}>{l.description}</div>
            </td>
            <td className="px-3 py-1.5 text-slate-500">{l.uom}</td>
            {/* Budget */}
            <td className="px-2 py-1 bg-blue-50/25">
              <NumCell value={l.qty} disabled={!editable} onChange={(v) => updateLine(idx, { qty: v })} testId={`boq-qty-${l.iow_code}`} />
            </td>
            <td className="px-2 py-1 bg-blue-50/25">
              <NumCell value={l.rate} disabled={!editable} onChange={(v) => updateLine(idx, { rate: v })} testId={`boq-rate-${l.iow_code}`} />
            </td>
            <td className="px-3 py-1.5 text-right font-semibold text-[#0D3B66] bg-blue-50/25">{fmtINR(d.amount)}</td>
            {/* GFC */}
            <td className="px-2 py-1">
              <NumCell value={l.gfc_qty} disabled={!editable} onChange={(v) => updateLine(idx, { gfc_qty: v })} testId={`boq-gfc-${l.iow_code}`} />
            </td>
            {/* JCR */}
            <td className="px-2 py-1 bg-emerald-50/25">
              <NumCell value={l.jcr_qty} disabled={!editable} onChange={(v) => updateLine(idx, { jcr_qty: v })} testId={`boq-jcrqty-${l.iow_code}`} />
            </td>
            <td className="px-2 py-1 bg-emerald-50/25">
              <NumCell value={l.jcr_rate} disabled={!editable} onChange={(v) => updateLine(idx, { jcr_rate: v })} testId={`boq-jcrrate-${l.iow_code}`} />
            </td>
            <td className="px-3 py-1.5 text-right font-semibold text-emerald-700 bg-emerald-50/25">{fmtINR(d.jcrAmount)}</td>
            {/* Balance / CTC (computed) */}
            <td className="px-3 py-1.5 text-right font-medium text-amber-700">{fmtINR(d.balanceAmount)}</td>
            <td className="px-3 py-1.5 text-right font-medium text-[#F97316]">{fmtINR(d.ctcAmount)}</td>
            {/* RA */}
            <td className="px-2 py-1 text-center">
              <button
                data-testid={`boq-ra-${l.iow_code}`}
                onClick={() => openRA(l.iow_code)}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition ${
                  ra && ra.entries?.length > 0
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    : "border-slate-200 bg-white text-slate-500 hover:border-[#F97316] hover:text-[#F97316]"
                }`}
                title="Rate Analysis"
              >
                <FlaskConical className="h-3 w-3" />
                {ra && ra.entries?.length > 0 ? `${ra.entries.length}` : "+"}
              </button>
            </td>
            {editable && (
              <td className="px-2 py-1 text-right">
                <button
                  data-testid={`boq-remove-${l.iow_code}`}
                  onClick={() => removeLine(idx)}
                  className="text-slate-400 hover:text-rose-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

function NumCell({ value, disabled, onChange, testId }) {
  return (
    <Input
      data-testid={testId}
      type="number"
      value={value ?? 0}
      disabled={disabled}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="h-8 text-right text-xs px-2 disabled:opacity-70 disabled:cursor-default"
    />
  );
}

function SORPicker({ open, onOpenChange, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const { data } = await api.get(`/sor`, { params: { search: q, limit: 100 } });
      setItems(data || []);
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0" data-testid="sor-picker-dialog">
        <div className="p-4 border-b border-slate-100">
          <DialogHeader><DialogTitle>Pick from SOR Master</DialogTitle></DialogHeader>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="sor-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search IOW code or item…"
              className="pl-9 h-10"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {items.map((s) => (
            <button
              key={s.iow_code}
              data-testid={`sor-pick-${s.iow_code}`}
              onClick={() => onPick(s)}
              className="w-full text-left px-4 py-2 border-b border-slate-100 hover:bg-slate-50 flex items-center justify-between"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{s.item_of_work}</div>
                <div className="text-xs text-slate-500">{s.iow_code} · {s.uom}</div>
              </div>
              <div className="text-sm font-semibold text-[#0D3B66]">{fmtINR(s.rate)}</div>
            </button>
          ))}
          {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No matches</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RateAnalysisDialog({ open, onOpenChange, projectId, iowCode, line, editable, initial, onSaved, onApplyRate }) {
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setEntries(initial?.entries ? [...initial.entries] : []);
  }, [open, initial, iowCode]);

  const raRate = useMemo(() => entries.reduce((a, e) => a + (Number(e.co_eff) || 0) * (Number(e.resource_rate) || 0), 0), [entries]);
  const perTypeTotals = useMemo(() => {
    const m = {};
    for (const e of entries) {
      const key = e.type || "Other";
      m[key] = (m[key] || 0) + (Number(e.co_eff) || 0) * (Number(e.resource_rate) || 0);
    }
    return m;
  }, [entries]);

  const addRow = () => setEntries((s) => [...s, { type: "Material", resource: "", resource_uom: line?.uom || "", resource_rate: 0, co_eff: 1 }]);
  const remove = (i) => setEntries((s) => s.filter((_, idx) => idx !== i));
  const update = (i, patch) => setEntries((s) => s.map((e, idx) => idx === i ? { ...e, ...patch } : e));

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const clean = entries.filter((e) => e.resource && e.resource.trim().length > 0);
      const { data } = await api.put(`/projects/${projectId}/rate-analysis/${iowCode}`, { entries: clean });
      onSaved(data);
      onOpenChange(false);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const applyRate = () => {
    onApplyRate(Number(raRate.toFixed(4)));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0" data-testid="ra-dialog">
        <div className="p-5 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-[#F97316]" /> Rate Analysis
            </DialogTitle>
          </DialogHeader>
          {line && (
            <div className="mt-2 text-sm text-slate-600">
              <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5 mr-2">{iowCode}</span>
              {line.description} · <span className="text-slate-500">per {line.uom}</span>
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {RA_TYPES.slice(0, 4).map((t) => (
              <div key={t} className="rounded-lg border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t}</div>
                <div className="text-sm font-bold text-[#0D3B66] mt-1">{fmtINR(perTypeTotals[t] || 0)}</div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <div className="col-span-2">Type</div>
              <div className="col-span-4">Resource</div>
              <div className="col-span-1">UOM</div>
              <div className="col-span-2 text-right">Res. Rate</div>
              <div className="col-span-1 text-right">Co-Eff</div>
              <div className="col-span-1 text-right">Cost/Unit</div>
              <div className="col-span-1"></div>
            </div>
            {entries.length === 0 && (
              <div data-testid="ra-empty" className="p-6 text-center text-sm text-slate-500">No resources yet. Add rows to build the rate.</div>
            )}
            {entries.map((e, i) => {
              const cost = (Number(e.co_eff) || 0) * (Number(e.resource_rate) || 0);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-slate-100 items-center" data-testid={`ra-row-${i}`}>
                  <div className="col-span-2">
                    <Select value={e.type} onValueChange={(v) => update(i, { type: v })} disabled={!editable}>
                      <SelectTrigger data-testid={`ra-type-${i}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RA_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Input data-testid={`ra-resource-${i}`} value={e.resource} disabled={!editable} onChange={(ev) => update(i, { resource: ev.target.value })} className="h-9 text-xs" placeholder="Resource description" />
                  </div>
                  <div className="col-span-1">
                    <Input data-testid={`ra-uom-${i}`} value={e.resource_uom} disabled={!editable} onChange={(ev) => update(i, { resource_uom: ev.target.value })} className="h-9 text-xs" placeholder="UOM" />
                  </div>
                  <div className="col-span-2">
                    <Input data-testid={`ra-rate-${i}`} type="number" value={e.resource_rate ?? 0} disabled={!editable} onChange={(ev) => update(i, { resource_rate: parseFloat(ev.target.value) || 0 })} className="h-9 text-xs text-right" />
                  </div>
                  <div className="col-span-1">
                    <Input data-testid={`ra-coeff-${i}`} type="number" value={e.co_eff ?? 0} disabled={!editable} onChange={(ev) => update(i, { co_eff: parseFloat(ev.target.value) || 0 })} className="h-9 text-xs text-right" step="0.0001" />
                  </div>
                  <div className="col-span-1 text-right text-xs font-semibold text-[#0D3B66]">{fmtINR(cost)}</div>
                  <div className="col-span-1 text-right">
                    {editable && (
                      <button data-testid={`ra-remove-${i}`} onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            {editable && (
              <Button data-testid="ra-add-row" onClick={addRow} variant="outline" size="sm" className="rounded-full">
                <Plus className="h-4 w-4 mr-1.5" /> Add resource
              </Button>
            )}
            <div className="flex items-center gap-4 ml-auto">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">RA Rate (sum of Cost/Unit)</div>
                <div data-testid="ra-total" className="text-2xl font-bold text-[#0D3B66]">₹{Number(raRate).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          {err && <div data-testid="ra-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{err}</div>}
        </div>

        <DialogFooter className="p-5 border-t border-slate-100">
          {editable && (
            <>
              <Button
                data-testid="ra-apply-rate"
                variant="outline"
                onClick={applyRate}
                disabled={saving || raRate <= 0}
                className="rounded-full"
              >
                <Calculator className="h-4 w-4 mr-1.5" /> Apply as BOQ Rate
              </Button>
              <Button data-testid="ra-save" onClick={save} disabled={saving} className="rounded-full bg-[#0D3B66] hover:bg-[#114B82]">
                {saving ? "Saving…" : "Save Rate Analysis"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
